/**
 * Builds the exact same PDF/print HTML used for facturas (logo, address, table, footer).
 * Use for: VistaFactura download, and when printing a venta that was converted from a factura.
 */
import type { Factura } from '../components/facturas/types';
import { numeroFacturaParaMostrar } from './facturaNumeroCanal';
import { getPaymentMethodQrUrl, getPaymentMethodDisplayFields } from './paymentMethodMedia';

function escHtml(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Etiqueta legible del método guardado en la factura (para el cliente). */
export function metodoPagoFacturaLabel(raw?: string | null): string {
  if (!raw || !String(raw).trim()) return '';
  const k = String(raw).toLowerCase().replace(/\s+/g, '_');
  const map: Record<string, string> = {
    efectivo: 'Efectivo',
    transferencia: 'Transferencia bancaria',
    tarjeta: 'Tarjeta',
    tarjeta_debito: 'Tarjeta débito',
    nequi: 'Nequi',
    daviplata: 'Daviplata',
    pse: 'PSE',
    credito: 'Crédito',
    pendiente: 'Por definir',
    otro: 'Otro',
  };
  return map[k] || String(raw).replace(/_/g, ' ');
}

export function estadoPagoFacturaLabel(ep?: string | null): string {
  if (!ep) return '';
  const m: Record<string, string> = {
    PENDIENTE_PAGO: 'Pago pendiente',
    PAGADO: 'Pagado',
    PAGO_PARCIAL: 'Pago parcial',
    PAGO_AL_ENTREGAR: 'Pago al entregar',
    CANCELADO: 'Cancelado',
  };
  return m[ep] || String(ep);
}

export type FacturaPdfPaymentMethod = {
  method_name: string;
  method_type?: string;
  icon?: string;
  description?: string;
  account_info?: Record<string, unknown>;
};

export interface FacturaPdfCompanyInfo {
  companyName: string;
  companyLogo: string | null;
  companyEmail: string;
  companyPhone: string;
  companyAddress: string;
  paymentMethods?: FacturaPdfPaymentMethod[];
}

export function buildFacturaPdfHtml(factura: Factura, info: FacturaPdfCompanyInfo): string {
  const {
    companyName,
    companyLogo,
    companyEmail,
    companyPhone,
    companyAddress,
    paymentMethods = [],
  } = info;

  const numeroMostrar = numeroFacturaParaMostrar(factura.numero, factura.canalVenta);

  const formatDate = (d: Date) => (d instanceof Date ? d : new Date(d)).toLocaleDateString('es-ES');
  const formatNum = (n: number) => n.toLocaleString('es-CO');

  return `<!DOCTYPE html>
<html>
  <head>
    <title>Factura ${numeroMostrar} - ${companyName}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
      .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
      .company-branding { display: flex; align-items: center; margin-bottom: 20px; }
      .company-logo { max-height: 60px; max-width: 200px; margin-right: 20px; }
      .company-logo-placeholder { height: 60px; width: 60px; margin-right: 20px; background: #3b82f6; color: white; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: bold; }
      .company-info h1 { font-size: 24px; margin: 0; color: #1f2937; }
      .company-details { font-size: 12px; color: #6b7280; margin-top: 5px; }
      .invoice-details { font-size: 14px; color: #6b7280; }
      .client-info { text-align: right; font-size: 14px; color: #6b7280; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
      th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
      th { background-color: #f9fafb; font-weight: bold; }
      .totals { width: 300px; margin-left: auto; }
      .totals div { display: flex; justify-content: space-between; margin-bottom: 5px; }
      .total-row { border-top: 2px solid #1f2937; padding-top: 10px; font-weight: bold; font-size: 18px; }
      .notes { margin-top: 30px; font-size: 14px; }
      .footer { margin-top: 50px; text-align: center; font-size: 12px; color: #6b7280; border-top: 1px solid #d1d5db; padding-top: 20px; }
      @media print { body { margin: 0; } }
    </style>
  </head>
  <body>
   <div class="company-branding">
     ${companyLogo ? `<img src="${companyLogo}" alt="${companyName}" class="company-logo" />` : `<div class="company-logo-placeholder">${companyName.charAt(0).toUpperCase()}</div>`}
     <div class="company-info">
       <h1>${companyName}</h1>
       <div class="company-details">
         ${companyEmail ? `<p><strong>Email:</strong> ${companyEmail}</p>` : ''}
         ${companyPhone ? `<p><strong>Teléfono:</strong> ${companyPhone}</p>` : ''}
         ${companyAddress ? `<p><strong>Dirección:</strong> ${companyAddress}</p>` : ''}
       </div>
     </div>
   </div>
   
    <div class="header">
      <div class="company-info">
        <h1>FACTURA</h1>
        <div class="invoice-details">
          <p><strong>Número:</strong> ${numeroMostrar}</p>
          <p><strong>Fecha:</strong> ${formatDate(factura.fechaCreacion)}</p>
          <p><strong>Entrega:</strong> ${formatDate(factura.fechaEntrega)}</p>
          ${
            factura.metodoPago
              ? `<p><strong>Forma de pago (esta factura):</strong> ${escHtml(metodoPagoFacturaLabel(factura.metodoPago))}</p>`
              : ''
          }
          ${
            factura.estadoPago
              ? `<p><strong>Estado de pago:</strong> ${escHtml(estadoPagoFacturaLabel(factura.estadoPago))}</p>`
              : ''
          }
        </div>
      </div>
      <div class="client-info">
        <p><strong>Estado:</strong> ${factura.estado}</p>
        <p><strong>Cliente:</strong> ${(`${factura.cliente?.nombre ?? ''} ${(factura.cliente as { apellido?: string })?.apellido ?? ''}`.trim()) || 'Sin cliente'}</p>
        <p><strong>Email:</strong> ${factura.cliente?.email || 'Sin email'}</p>
        <p><strong>Teléfono:</strong> ${factura.cliente?.telefono || 'Sin teléfono'}</p>
        ${factura.cliente?.numeroDocumento ? `<p><strong>Documento:</strong> ${(factura.cliente as { tipoDocumento?: string })?.tipoDocumento || 'CC'}: ${factura.cliente.numeroDocumento}</p>` : ''}
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Producto/Servicio</th>
          <th>Descripción</th>
          <th style="text-align: center;">Cantidad</th>
          <th style="text-align: right;">Precio Unitario</th>
          <th style="text-align: right;">Subtotal</th>
        </tr>
      </thead>
      <tbody>
        ${(factura.items || []).map((item: { producto: string; descripcion: string; cantidad: number; precioUnitario: number; subtotal: number }) => `
          <tr>
            <td>${item.producto}</td>
            <td>${item.descripcion}</td>
            <td style="text-align: center;">${item.cantidad}</td>
            <td style="text-align: right;">$${formatNum(item.precioUnitario)}</td>
            <td style="text-align: right;">$${formatNum(item.subtotal)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>

    <div class="totals">
      <div>
        <span>Subtotal sin IVA:</span>
        <span>$${formatNum(factura.subtotalSinIva ?? 0)}</span>
      </div>
      <div>
        <span>IVA:</span>
        <span>$${formatNum(factura.totalIva ?? 0)}</span>
      </div>
      <div>
        <span>Subtotal con IVA:</span>
        <span>$${formatNum(factura.subtotal ?? 0)}</span>
      </div>
      ${(factura.descuento ?? 0) > 0 ? `
      <div style="color: #059669;">
        <span>Descuento:</span>
        <span>-$${formatNum(factura.descuento)}</span>
      </div>
      ` : ''}
      ${(factura.costoEnvio ?? 0) > 0 ? `
      <div>
        <span>Envío:</span>
        <span>$${formatNum(factura.costoEnvio ?? 0)}</span>
      </div>
      ` : ''}
      <div class="total-row">
        <span>Total:</span>
        <span>$${formatNum(factura.total ?? 0)}</span>
      </div>
      ${(factura.montoPagado ?? 0) > 0 ? `
      <div style="margin-top:8px;">
        <span>Pagado:</span>
        <span>$${formatNum(factura.montoPagado ?? 0)}</span>
      </div>
      ` : ''}
      ${(factura.montoPendiente ?? 0) > 0 && String(factura.estadoPago || '').toUpperCase() !== 'PAGADO' ? `
      <div>
        <span>Saldo pendiente:</span>
        <span>$${formatNum(factura.montoPendiente ?? 0)}</span>
      </div>
      ` : ''}
    </div>

    ${factura.notas || factura.condiciones ? `
    <div class="notes">
      ${factura.notas ? `<p><strong>Notas:</strong> ${factura.notas}</p>` : ''}
      ${factura.condiciones ? `<p><strong>Condiciones de Pago:</strong> ${factura.condiciones}</p>` : ''}
    </div>
    ` : ''}

    ${paymentMethods.length > 0 ? `
    <div class="notes" style="margin-top: 30px;">
      <h3 style="font-weight: bold; margin-bottom: 15px; font-size: 16px;">💳 Métodos de Pago Disponibles:</h3>
      ${paymentMethods.map((method: FacturaPdfPaymentMethod) => {
        const acc = method?.account_info as Record<string, unknown> | undefined;
        const details = getPaymentMethodDisplayFields(acc)
          .map((f) => {
            const label = escHtml(f.label);
            const value = f.href
              ? `<a href="${escHtml(f.href)}" target="_blank" rel="noopener noreferrer">${escHtml(f.value)}</a>`
              : escHtml(f.value);
            return `${label}: ${value}`;
          })
          .join('<br>');
        const qrUrl = getPaymentMethodQrUrl(acc);
        const qrImage = qrUrl
          ? `<div style="margin-top: 10px;"><img src="${escHtml(qrUrl)}" alt="Código QR" style="max-width: 150px; max-height: 150px; border: 1px solid #e5e7eb; border-radius: 4px; padding: 8px; background: white;" /></div>`
          : '';
        return `<div style="margin-bottom: 15px; padding: 10px; background-color: #f9fafb; border-left: 3px solid #3b82f6;">
          <strong>${method?.icon || '💳'} ${escHtml(String(method?.method_name || 'Método de pago'))}</strong>
          ${method?.description ? `<div style="margin-top: 4px; font-size: 13px; color: #6b7280;">${escHtml(String(method.description))}</div>` : ''}
          ${details ? `<div style="margin-top: 5px; font-size: 13px; color: #6b7280;">${details}</div>` : ''}
          ${qrImage}
        </div>`;
      }).join('')}
    </div>
    ` : ''}

    <div class="footer">
      <p>Esta factura fue generada por ${companyName}</p>
      <p>Para consultas, contacta a tu representante de ventas</p>
      ${companyEmail ? `<p>Email: ${companyEmail}</p>` : ''}
      ${companyPhone ? `<p>Teléfono: ${companyPhone}</p>` : ''}
    </div>
  </body>
</html>`;
}
